export interface HelpArticle {
    id: string;
    title: string;
    description: string;
    content: string;
    category: string;
    tags: string[];
    lastUpdated: string;
}

export interface HelpCategory {
    id: string;
    title: string;
    description: string;
    icon: string;
    articleIds: string[];
}

export const HELP_DATA: { categories: HelpCategory[]; articles: HelpArticle[] } = {
    categories: [
        {
            id: 'getting-started',
            title: 'Getting Started',
            description: 'Master the core mechanics and set up your workspace for success.',
            icon: 'Rocket',
            articleIds: ['onboarding-guide', 'dashboard-navigation', 'universal-search']
        },
        {
            id: 'ai-mastery',
            title: 'AI Mastery',
            description: 'Leverage the power of Vicra AI to automate summaries, diagrams, and flashcards.',
            icon: 'Brain',
            articleIds: ['ai-smart-notebooks', 'contextual-ai-queries', 'ai-generated-outputs']
        },
        {
            id: 'canvas-mastery',
            title: 'Canvas Mastery',
            description: 'Deep dive into infinite navigation, layered organization, and professional drawing.',
            icon: 'Layout',
            articleIds: ['infinite-canvas-navigation', 'bento-grid-layout', 'precision-drawing-engine', 'exporting-workspaces']
        },
        {
            id: 'note-types',
            title: 'Note Ecosystem',
            description: 'Explore the 20+ different note types, from Python runtimes to interactive polls.',
            icon: 'BookOpen',
            articleIds: ['python-runtime', 'multimedia-integration', 'interactive-polls']
        },
        {
            id: 'collaboration',
            title: 'Team Collaboration',
            description: 'Learn how to share boards, leave comments, and collaborate in real-time.',
            icon: 'MessageSquare',
            articleIds: ['realtime-collaboration', 'permission-management', 'commenting-workflows']
        }
    ],
    articles: [
        {
            id: 'onboarding-guide',
            title: 'NoteSpace Onboarding Guide',
            description: 'The definitive guide to your first 5 minutes in NoteSpace.',
            category: 'getting-started',
            tags: ['basics', 'intro', 'setup'],
            lastUpdated: '2024-01-16',
            content: `
# Your First 5 Minutes in NoteSpace 🚀

NoteSpace is more than just a note-taking app; it is a **visual thought engine**. This guide will walk you through the essential steps to get your ideas flowing.

## 1. Creating Your First Board
When you land in your Dashboard, click the **"New Board"** button. You can choose a blank canvas or start with one of our curated templates for brainstorming, project planning, or research.

## 2. Double-Click to Create
The fastest way to add content is to **double-click anywhere** on the canvas. This creates a standard text note. 

## 3. The Power of Drag-and-Drop
NoteSpace is multi-modal. You can drag images, PDFs, or even code snippets directly from your desktop onto the canvas. We will automatically format them for you.

## 4. Zoom and Pan
- **Pan**: Use your mouse to drag the background or hold the **Spacebar** while dragging.
- **Zoom**: Use your scroll wheel or the trackpad pinch gesture. You have up to 2000% zoom for deep detail and 1% for a bird's-eye view.

### Quick Start Tip
Try the **Command Palette (Ctrl/Cmd + K)** to quickly jump between boards or search for any piece of text across your entire workspace.
`
        },
        {
            id: 'universal-search',
            title: 'Using Universal Search',
            description: 'Find anything across boards, notes, and AI outputs instantly.',
            category: 'getting-started',
            tags: ['search', 'navigation', 'speed'],
            lastUpdated: '2024-01-16',
            content: `
# Search Without Limits 🔍

As your knowledge base grows, finding specific information quickly is vital. NoteSpace features a **Universal Search** engine that indexes everything you create.

## How to Trigger
- Click the **Search Icon** in the Dashboard sidebar.
- Use the keyboard shortcut: **Cmd + K** (Mac) or **Ctrl + K** (Windows/Linux).

## What can you search?
1. **Board Titles**: Quickly jump between different canvases.
2. **Note Content**: We search the text inside every Sticky, Rich Text, and Code note.
3. **Tags**: If you tag your notes, you can filter search results by specific labels.
4. **AI Outputs**: Find previously generated summaries or flashcards by keywords.

## Panning to Results
When you search from *inside* a board, clicking a result will not just open the note—it will **smoothly pan and zoom** the canvas right to it. This "contextual search" ensures you never lose the bigger picture.
`
        },
        {
            id: 'dashboard-navigation',
            title: 'Navigating the Dashboard',
            description: 'Manage your boards, favorites, and trash with ease.',
            category: 'getting-started',
            tags: ['dashboard', 'organization', 'favorites'],
            lastUpdated: '2024-01-16',
            content: `
# Mastering the Dashboard 🏠

The Dashboard is your mission control. It is designed to help you organize hundreds of boards without feeling overwhelmed.

## The Sidebar
- **Home**: View all your recent boards in a grid or list view.
- **Favorites**: Quickly access boards you have "Starred".
- **Trash**: Deleted boards are kept here for 30 days before being permanently removed.
- **AI View**: The entry point for your Smart Notebooks and Vicra AI exports.

## Workspace Settings
The "Workspace" icon in the bottom left of the sidebar allows you to manage your **Brand Kit**, change themes, and configure default font settings for all new notes.
`
        },
        {
            id: 'ai-smart-notebooks',
            title: 'Mastering Smart Notebooks',
            description: 'Use the AI-powered side of NoteSpace to turn notes into structured knowledge.',
            category: 'ai-mastery',
            tags: ['ai', 'notebooks', 'vicra'],
            lastUpdated: '2024-01-16',
            content: `
# Mastering Smart Notebooks 🧠

Smart Notebooks are the core of NoteSpace's "second brain" philosophy. They allow you to feed content into our **Vicra AI engine** to generate high-level insights.

## Generating Outputs
Once your sources are selected, you can ask Vicra to:
- **Summarize**: Get a 3-paragraph executive summary of 50+ notes.
- **Flashcards**: Automatically generate question-answer pairs for studying.
- **Timeline**: Turn scattered meeting notes into a chronological project timeline.
- **Mermaid Diagrams**: Ask for a flow-chart or mind-map based on your text.

## Chatting with Sources
The **Chat Panel** in a Smart Notebook only uses the selected sources to answer questions. This eliminates AI hallucinations and ensures accuracy for your research and project work.
`
        },
        {
            id: 'bento-grid-layout',
            title: 'Using the Bento Grid System',
            description: 'Organize your thoughts into beautiful, structured layouts automatically.',
            category: 'canvas-mastery',
            tags: ['layout', 'bento', 'organization'],
            lastUpdated: '2024-01-16',
            content: `
# The Bento Grid System 🍱

NoteSpace introduces the **Bento Layout Engine**, which helps you turn messy clusters of notes into organized, high-density grids with a single click.

## Why Bento?
Bento grids are mathematically optimized to fit different note sizes into a compact, visually pleasing rectangle. It’s perfect for:
- Creating "Summary Cards" for a board.
- Organizing research references.
- Building a "Visual Index" of project milestones.

## How to Apply
1. Select a group of notes on your canvas.
2. In the **Selection Action Bar**, look for the **Bento Icon** (a 4-square grid).
3. NoteSpace will automatically resize and snap your notes into a perfect Bento grid.

## Customizing the Grid
Once a Bento grid is created, you can drag individual items to "swap" their positions within the layout. The grid will intelligently resize to accommodate the new arrangement.
`
        },
        {
            id: 'exporting-workspaces',
            title: 'Exporting Your Work',
            description: 'Turn your canvas into high-quality PDFs, images, or Markdown files.',
            category: 'canvas-mastery',
            tags: ['export', 'pdf', 'sharing'],
            lastUpdated: '2024-01-16',
            content: `
# Take Your Ideas Anywhere 📤

NoteSpace doesn't lock your data in. We provide robust export tools so you can share your work on any platform.

## Supported Formats
- **PDF**: Best for sharing project plans or multi-page slide decks created with Frames.
- **Portable PNG**: High-resolution image export. You can choose to export the **entire board** or just a **selected region**.
- **Markdown**: Export your text and code notes as standard .md files for use in Obsidian, Notion, or GitHub.

## How to Export
1. Click the **Export Modal** icon in the toolbar.
2. Select your desired format.
3. Choose the quality and "Include Background" settings.
4. Click **"Download"**.

### Pro Tip
If you have used **Frames** to organize your work, exporting to PDF will automatically treat each Frame as a separate page, creating a perfectly formatted document.
`
        },
        {
            id: 'python-runtime',
            title: 'Running Python on Canvas',
            description: 'Execute code, visualize data, and perform calculations directly inside your notes.',
            category: 'note-types',
            tags: ['python', 'code', 'data science'],
            lastUpdated: '2024-01-16',
            content: `
# Python Code Runtimes 🐍

NoteSpace includes a built-in Python runtime that allows you to execute code directly on your canvas. This is perfect for data scientists, students, and engineers.

## Creating a Python Note
Select the **Code Tool** from the toolbar and pick **"Python"** as the language. A specialized note will appear with an "Execute" button.

## Features
- **Library Support**: We support common libraries like \`numpy\`, \`pandas\`, and \`matplotlib\`.
- **Inline Visualization**: If your code generates a chart (e.g., using matplotlib), NoteSpace will render the image directly inside the note.
`
        },
        {
            id: 'precision-drawing-engine',
            title: 'The Precision Drawing Engine',
            description: 'Professional vector tools for artists and engineers.',
            category: 'canvas-mastery',
            tags: ['drawing', 'vector', 'pen'],
            lastUpdated: '2024-01-16',
            content: `
# Professional Drawing Tools 🖊️

NoteSpace uses a precision vector engine. Unlike pixel-based drawing apps, your sketches in NoteSpace stay perfectly sharp even at 2000% zoom.

## The Toolset
- **High-Pressure Pen**: Supports pressure sensitivity for a natural handwriting feel.
- **Shape Snapping**: Draw a rough circle and hold for a second; NoteSpace will snap it into a perfect geometric shape.

## Connectors & Flow
Our **Smart Connector** tool allows you to link any two notes. These lines are "sticky"—if you move a note, the line automatically adjusts its path and anchor points to stay connected.
`
        },
        {
            id: 'interactive-polls',
            title: 'Interactive Polls & Feedback',
            description: 'Engage your team with live voting and tactile UI elements.',
            category: 'note-types',
            tags: ['polls', 'interaction', 'checklists'],
            lastUpdated: '2024-01-16',
            content: `
# Interactive Note Types 🗳️

NoteSpace boards aren't static. We provide several note types that allow for live interaction and data gathering.

## Poll Notes
Create a **Poll Note** to gather feedback from your team. Collaborators can click their choice, and the results update in real-time with smooth animations.

## Checklist & Planner Notes
The **Planner Note** is a powerful tool for project management.
- **Tactile Feedback**: Click to check items off.
- **Progress Tracking**: See a visual progress bar that updates as your team completes tasks.

## Sticky Notes
Perfect for brainstorms. They have a limited character count to keep ideas concise and high-impact.
`
        },
        {
            id: 'realtime-collaboration',
            title: 'Real-Time Collaboration',
            description: 'Working together in NoteSpace is as fluid as working alone.',
            category: 'collaboration',
            tags: ['collaboration', 'teams', 'live'],
            lastUpdated: '2024-01-16',
            content: `
# Working Together in Real-Time 🤝

NoteSpace is built for teams. Whether you are conducting a remote workshop or co-authoring a research paper, collaboration is seamless.

## Live Indicators
When others are on your board, you will see:
- **Collaborator Cursors**: See exactly where your team is looking and what they are interacting with.
- **Typing Indicators**: Know when a teammate is editing a note to avoid conflicts.
- **Presence Bar**: A list of active users in the top bar. Click their avatar to **"Teleport"** to their view.

## Commenting & Feedback
Right-click any note or empty space to **"Add Comment"**. You can @mention teammates to trigger notifications.
`
        }
    ]
};
